import React from 'react';
import { Card, CardContent } from './ui/card';
import { Phone, Mail, MessageCircle, Instagram, MapPin } from 'lucide-react';
import { contactInfo } from '../mockData';
import MultiStepBooking from './MultiStepBooking';

const Contact = () => {
  return (
    <section id="contact" className="py-10 sm:py-16 bg-gradient-to-b from-blue-50 to-white">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-12">
        {/* Booking Section Header */}
        <div className="mb-6">
          <div className="text-xs text-gray-500 mb-2">
            <span>Home</span> <span className="mx-1">›</span> <span>Online Transfer Booking</span>
          </div>
          <h2 className="text-3xl sm:text-5xl font-serif font-bold text-[#1a2b4a]" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>
            Online Transfer Booking
          </h2>
        </div>

        {/* Multi-Step Booking Form */}
        <div className="mb-16">
          <MultiStepBooking />
        </div>

        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Get in <span className="text-blue-600">Touch</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Have questions? Reach out anytime — we're here 24/7.
          </p>
        </div>

        {/* Contact Methods Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-12">
          {/* WhatsApp */}
          <Card className="border-2 border-green-200 hover:border-green-400 transition-all duration-300 hover:shadow-xl">
            <CardContent className="p-6">
              <div className="flex flex-col items-center text-center">
                <div className="w-14 h-14 bg-green-500 rounded-full flex items-center justify-center mb-4">
                  <MessageCircle className="text-white" size={26} />
                </div>
                <h3 className="font-bold text-gray-900 mb-1">WhatsApp</h3>
                <p className="text-sm text-gray-600 mb-3">Quick booking & support</p>
                <a 
                  href={`https://wa.me/${contactInfo.whatsapp.replace(/[^0-9]/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-600 font-semibold hover:text-green-700"
                >
                  {contactInfo.whatsapp}
                </a>
              </div>
            </CardContent>
          </Card>

          {/* Phone */}
          <Card className="border-2 border-blue-200 hover:border-blue-400 transition-all duration-300 hover:shadow-xl">
            <CardContent className="p-6">
              <div className="flex flex-col items-center text-center">
                <div className="w-14 h-14 bg-blue-500 rounded-full flex items-center justify-center mb-4">
                  <Phone className="text-white" size={26} />
                </div>
                <h3 className="font-bold text-gray-900 mb-1">Phone</h3>
                <p className="text-sm text-gray-600 mb-3">Call us anytime 24/7</p>
                <a 
                  href={`tel:${contactInfo.phone}`}
                  className="text-blue-600 font-semibold hover:text-blue-700"
                >
                  {contactInfo.phone}
                </a>
              </div>
            </CardContent>
          </Card>

          {/* Email */}
          <Card className="border-2 border-purple-200 hover:border-purple-400 transition-all duration-300 hover:shadow-xl">
            <CardContent className="p-6">
              <div className="flex flex-col items-center text-center">
                <div className="w-14 h-14 bg-purple-500 rounded-full flex items-center justify-center mb-4">
                  <Mail className="text-white" size={26} />
                </div>
                <h3 className="font-bold text-gray-900 mb-1">Email</h3>
                <p className="text-sm text-gray-600 mb-3">Send us a message</p>
                <a 
                  href={`mailto:${contactInfo.email}`}
                  className="text-purple-600 font-semibold hover:text-purple-700 break-all text-sm"
                >
                  {contactInfo.email}
                </a>
              </div>
            </CardContent>
          </Card>

          {/* Instagram */}
          <Card className="border-2 border-pink-200 hover:border-pink-400 transition-all duration-300 hover:shadow-xl">
            <CardContent className="p-6">
              <div className="flex flex-col items-center text-center">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mb-4">
                  <Instagram className="text-white" size={26} />
                </div>
                <h3 className="font-bold text-gray-900 mb-1">Instagram</h3>
                <p className="text-sm text-gray-600 mb-3">Follow our journey</p>
                <a 
                  href={`https://instagram.com/${contactInfo.instagram.replace('@', '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-pink-600 font-semibold hover:text-pink-700"
                >
                  {contactInfo.instagram}
                </a>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Google Maps */}
        <Card className="border-2 border-gray-200 overflow-hidden max-w-5xl mx-auto">
          <CardContent className="p-0">
            <div className="bg-gradient-to-r from-blue-600 to-cyan-500 p-4 flex items-center space-x-3">
              <MapPin className="text-white" size={24} />
              <h3 className="text-xl font-bold text-white">Our Location</h3>
            </div>
            <div className="aspect-video bg-gray-100">
              <iframe
                title="Golden Corfu Transfer Location"
                src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3087.547!2d${contactInfo.mapCoordinates.lng}!3d${contactInfo.mapCoordinates.lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMznCsDM2JzA2LjgiTiAxOcKwNTQnNDIuMSJF!5e0!3m2!1sen!2s!4v1234567890`}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default Contact;
